import { useState } from 'react'

import { useTranslation } from 'react-i18next'
import {
  useMutation,
  useQuery,
  useQueryClient
} from 'react-query'
import BeatLoader from 'react-spinners/BeatLoader'
import styled from 'styled-components'

import { IconTrashcan } from 'assets/svg/sprite'
import { addToast } from 'components/Toast/Toast'
import {
  addUserNote,
  deleteUserNote,
  getUserNotes
} from 'services/endpoints/profile'
import colors from 'styles/colors'

export interface NewNote {
  text: string
  user_id: number
}

interface UserNotesProps {
  user_id: number
  userPublicId: string
}

export function UserNotes({
  user_id,
  userPublicId
}: UserNotesProps) {
  const [noteBody, setNoteBody] =
    useState<string>('')

  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const userNotesQueryKey = [
    'userNotes',
    userPublicId
  ] as const

  const addNote = useMutation(
    async (note: NewNote) =>
      await addUserNote(note),
    {
      onSuccess: () => {
        setNoteBody('')

        queryClient.invalidateQueries(
          userNotesQueryKey
        )
      },

      onError: () => {
        addToast(
          'error',
          t('anErrorOccuredPleaseTryAgain')
        )

        queryClient.invalidateQueries(
          userNotesQueryKey
        )
      }
    }
  )

  const deleteNote = useMutation(
    async (id: number) =>
      await deleteUserNote(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(
          userNotesQueryKey
        )
      },

      onError: () => {
        addToast(
          'error',
          t('anErrorOccuredPleaseTryAgain')
        )

        queryClient.invalidateQueries(
          userNotesQueryKey
        )
      }
    }
  )

  const {
    data: notes,
    isLoading,
    isFetching
  } = useQuery(
    userNotesQueryKey,
    async () =>
      await getUserNotes(userPublicId),
    {
      enabled: !!userPublicId
    }
  )

  const handleDeleteNote = (
    id: number
  ) => {
    deleteNote.mutate(id)
  }

  const handleAddNote = (
    body: string
  ) => {
    const trimmedBody = body.trim()

    if (!trimmedBody) {
      return
    }

    addNote.mutate({
      text: trimmedBody,
      user_id
    })
  }

  const showLoader =
    isLoading &&
    !notes

  return (
    <Container>
      <AddNote>
        <AddNoteInput
          value={noteBody}
          onChange={e => {
            setNoteBody(
              e.currentTarget.value
            )
          }}
          placeholder='Add Note...'
        />

        <Save
          type='button'
          onClick={() => {
            handleAddNote(noteBody)
          }}
          disabled={addNote.isLoading}
        >
          Add
        </Save>
      </AddNote>

      <NotesSection>
        {showLoader ? (
          <LoaderRow>
            <BeatLoader size={8} />
          </LoaderRow>
        ) : (
          <>
            {notes?.map(note => {
              const date =
                new Date(note.created_at)

              return (
                <Note key={note.id}>
                  <NoteContent>
                    <NoteBody>
                      {note.text}
                    </NoteBody>

                    <DeleteButton
                      type='button'
                      onClick={() => {
                        handleDeleteNote(
                          note.id
                        )
                      }}
                      disabled={
                        deleteNote.isLoading
                      }
                    >
                      <IconTrashcan
                        width='18px'
                        height='18px'
                      />
                    </DeleteButton>
                  </NoteContent>

                  <NoteDate>
                    {date.toLocaleString(
                      'en-us',
                      {
                        month: 'short',
                        day: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true
                      }
                    )}
                  </NoteDate>
                </Note>
              )
            })}

            {!notes?.length &&
              !isFetching && (
                <EmptyState>
                  No notes yet.
                </EmptyState>
              )}
          </>
        )}
      </NotesSection>
    </Container>
  )
}

/* =================== styles =================== */

const LoaderRow = styled.div`
  width: 100%;

  display: flex;
  justify-content: center;

  padding: 3rem 0 2rem;
`

const Container = styled.div`
  height: 50%;
  width: 100%;

  box-sizing: border-box;

  background-color: ${colors.white};
  background: ${colors.white89deg};

  border-radius: 2.5rem;

  position: absolute;
  bottom: 0;
  right: 0;

  z-index: 1;

  padding: 2rem;

  display: flex;
  flex-direction: column;
  gap: 2rem;

  -webkit-overflow-scrolling: touch;
`

const AddNote = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  gap: 1rem;

  flex-shrink: 0;
`

const AddNoteInput = styled.input`
  width: 100%;
  height: 4.5rem;

  border: 0.1rem solid #dddddd;
  border-radius: 1.5rem;

  padding: 1.2rem;

  font-family: Montserrat;
  font-size: 10px;
  font-weight: 500;
  line-height: 15px;
  letter-spacing: 0;
  text-align: left;

  outline: none;

  &::placeholder {
    color: #b3b3b3;

    opacity: 1;

    font-weight: 400;
    font-size: 1.1rem;
  }
`

const Save = styled.button`
  background: ${colors.blue92deg};
  color: ${colors.white};

  border: none;
  border-radius: 1.5rem;

  font-size: 1.2rem;
  font-weight: 500;

  padding: 1rem 2rem;

  cursor: pointer;

  flex-shrink: 0;

  height: 4.5rem;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`

const NotesSection = styled.div`
  width: 100%;

  flex: 1;

  min-width: 0;
  min-height: 0;

  display: flex;
  flex-direction: column;

  overflow-y: auto;
  overflow-x: hidden;

  padding-bottom: 2rem;
`

const Note = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  gap: 0.5rem;

  &:not(:last-child) {
    margin-bottom: 1.2rem;
  }
`

const NoteContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  width: 100%;
  min-width: 0;

  gap: 1rem;
`

const DeleteButton = styled.button`
  flex-shrink: 0;

  cursor: pointer;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  padding: 0;

  border: 0;
  background: transparent;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`

const NoteBody = styled.div`
  width: 100%;

  background-color: ${colors.whiteSmoke};

  font-family: Montserrat;
  font-size: 14px;
  font-weight: 500;
  line-height: 17px;
  letter-spacing: 0.04em;
  text-align: left;

  word-break: break-word;
  overflow-wrap: anywhere;

  color: #757576;

  border-radius: 2rem;

  padding: 1.25rem 2rem;
`

const NoteDate = styled.div`
  width: 100%;

  font-family: Montserrat;
  font-size: 11px;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 0;
  text-align: right;

  color: ${colors.silverGray};

  padding-left: 1.25rem;
  padding-right: 3.25rem;
`

const EmptyState = styled.div`
  flex: 1;

  display: flex;
  align-items: center;
  justify-content: center;

  font-family: Montserrat;
  font-size: 14px;
  font-weight: 500;

  color: ${colors.silverGray};
`